import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Banner, BannerCreateData, BannerUpdateData } from "@/types/banner";
import { fetchAllBanners, createBanner, updateBanner, deleteBanner, uploadBannerImage } from "@/services/bannersService";
import { BannerImagePositionEditor } from "@/components/admin/banners/BannerImagePositionEditor";
import { Plus, Edit, Trash2, Image as ImageIcon, Smartphone, Monitor, ExternalLink } from "lucide-react";

export const BannersAdmin: React.FC = () => {
  const { toast } = useToast();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    subtitle: "",
    link_url: "",
    link_text: "",
    page: "mentores" as "mentores" | "jogos" | "mentorias" | "comunidades",
    is_active: true,
    display_order: 1,
    desktop_image_position: "center" as "center" | "top" | "bottom" | "left" | "right" | "custom",
    desktop_image_object_position: "center center",
    desktop_image_scale: 1
  });
  
  const [desktopFile, setDesktopFile] = useState<File | null>(null);
  const [mobileFile, setMobileFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const data = await fetchAllBanners();
      console.log('🔄 [fetchBanners] Banners carregados do banco:', data.map(banner => ({
        id: banner.id,
        title: banner.title,
        desktop_image_position: banner.desktop_image_position,
        desktop_image_object_position: banner.desktop_image_object_position,
        desktop_image_scale: banner.desktop_image_scale
      })));
      setBanners(data);
    } catch (error) {
      console.error("Error in fetchBanners:", error);
      toast({
        title: "Erro",
        description: "Erro ao carregar banners. A tabela pode ainda não ter sido criada.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      subtitle: "",
      link_url: "",
      link_text: "",
      page: "mentores",
      is_active: true,
      display_order: 1,
      desktop_image_position: "center",
      desktop_image_object_position: "center center",
      desktop_image_scale: 1
    });
    setDesktopFile(null);
    setMobileFile(null);
    setEditingBanner(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (banner: Banner) => {
    console.log('🔧 [openEditDialog] Banner original:', {
      id: banner.id,
      title: banner.title,
      desktop_image_position: banner.desktop_image_position,
      desktop_image_object_position: banner.desktop_image_object_position,
      desktop_image_scale: banner.desktop_image_scale
    });

    const formDataToSet = {
      title: banner.title,
      subtitle: banner.subtitle || "",
      link_url: banner.link_url || "",
      link_text: banner.link_text || "",
      page: banner.page,
      is_active: banner.is_active,
      display_order: banner.display_order,
      desktop_image_position: banner.desktop_image_position || "center",
      desktop_image_object_position: banner.desktop_image_object_position || "center center",
      desktop_image_scale: banner.desktop_image_scale || 1
    };

    console.log('📝 [openEditDialog] FormData que será definido:', {
      desktop_image_position: formDataToSet.desktop_image_position,
      desktop_image_object_position: formDataToSet.desktop_image_object_position,
      desktop_image_scale: formDataToSet.desktop_image_scale
    });

    setFormData(formDataToSet);
    setEditingBanner(banner);
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || (!desktopFile && !editingBanner)) {
      toast({
        title: "Erro",
        description: "Título e imagem desktop são obrigatórios",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      let desktopImageUrl = editingBanner?.image_url || "";
      let mobileImageUrl = editingBanner?.mobile_image_url || "";

      // Upload desktop image
      if (desktopFile) {
        desktopImageUrl = await uploadBannerImage(desktopFile, false);
      }

      // Upload mobile image if provided
      if (mobileFile) {
        mobileImageUrl = await uploadBannerImage(mobileFile, true);
      }

      const bannerData = {
        ...formData,
        image_url: desktopImageUrl,
        mobile_image_url: mobileImageUrl || undefined
      };

      console.log('💾 [handleSubmit] Dados que serão salvos:', {
        desktop_image_position: bannerData.desktop_image_position,
        desktop_image_object_position: bannerData.desktop_image_object_position,
        desktop_image_scale: bannerData.desktop_image_scale
      });

      if (editingBanner) {
        console.log('✏️ [handleSubmit] Atualizando banner existente:', editingBanner.id);
        await updateBanner(editingBanner.id, bannerData as BannerUpdateData);
        toast({
          title: "Sucesso",
          description: "Banner atualizado com sucesso!"
        });
      } else {
        console.log('➕ [handleSubmit] Criando novo banner');
        await createBanner(bannerData as BannerCreateData);
        toast({
          title: "Sucesso",
          description: "Banner criado com sucesso!"
        });
      }

      setIsDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      console.error("Error saving banner:", error);
      toast({
        title: "Erro",
        description: error instanceof Error && error.message.includes('does not exist') 
          ? "A tabela de banners ainda não foi criada. Entre em contato com o administrador."
          : "Erro ao salvar banner",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este banner?")) {
      return;
    }

    try {
      await deleteBanner(id);
      toast({
        title: "Sucesso",
        description: "Banner excluído com sucesso!"
      });
      fetchBanners();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao excluir banner",
        variant: "destructive"
      });
    }
  };

  const getPageLabel = (page: string) => {
    const labels = {
      mentores: "Mentores",
      jogos: "Jogos",
      mentorias: "Mentorias",
      comunidades: "Comunidades"
    };
    return labels[page as keyof typeof labels] || page;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-white">Carregando banners...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Gerenciar Banners</h1>
        <Button onClick={openCreateDialog} className="bg-[#00B6F0] hover:bg-[#0095c7]">
          <Plus className="w-4 h-4 mr-2" />
          Novo Banner
        </Button>
      </div>

      {/* Lista de Banners */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {banners.map((banner) => (
          <Card key={banner.id} className="bg-[#181D24] border-[#222A35]">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-white text-lg">{banner.title}</CardTitle>
                  <div className="flex gap-2 mt-2">
                    <Badge variant={banner.is_active ? "default" : "secondary"}>
                      {banner.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                    <Badge variant="outline" className="text-[#00B6F0]">
                      {getPageLabel(banner.page)}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(banner)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <img
                  src={banner.image_url}
                  alt={banner.title}
                  className="w-full h-32 object-cover rounded-md"
                />
                
                {banner.subtitle && (
                  <p className="text-gray-400 text-sm">{banner.subtitle}</p>
                )}
                
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>Ordem: {banner.display_order}</span>
                  {banner.mobile_image_url && (
                    <Badge variant="outline" className="text-xs">
                      <Smartphone className="w-3 h-3 mr-1" />
                      Mobile
                    </Badge>
                  )}
                  {banner.link_url && (
                    <Badge variant="outline" className="text-xs">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Link
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {banners.length === 0 && (
        <Card className="bg-[#181D24] border-[#222A35]">
          <CardContent className="text-center py-8">
            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Sistema de Banners</h3>
            <p className="text-gray-400 mb-4">
              {loading ? "Carregando banners..." : "Nenhum banner encontrado"}
            </p>
            {!loading && (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Se esta é a primeira vez acessando esta página, talvez seja necessário criar a tabela de banners no banco de dados.
                </p>
                <div className="bg-[#2A3441] p-4 rounded-lg text-left mb-4">
                  <p className="text-xs text-gray-400 mb-2">Para configurar o sistema:</p>
                  <ol className="text-xs text-gray-300 space-y-1">
                    <li>1. Vá para o painel do Supabase</li>
                    <li>2. Acesse SQL Editor</li>
                    <li>3. Execute o arquivo <code className="bg-[#181D24] px-2 py-1 rounded">create_banners_table.sql</code></li>
                    <li>4. Recarregue esta página</li>
                  </ol>
                </div>
                <Button 
                  onClick={openCreateDialog} 
                  className="mt-4 bg-[#00B6F0] hover:bg-[#0095c7]"
                >
                  Tentar Criar Banner
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent 
          className="bg-[#1E293B] text-white border-[#2A3749] max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? "Editar Banner" : "Novo Banner"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Título */}
            <div>
              <Label htmlFor="title">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-[#2A3441] border-[#3A4451] text-white"
                placeholder="Ex: Banner Promocional Mentores"
                required
              />
              <p className="text-sm text-gray-400 mt-1">
                Apenas para identificação no painel administrativo
              </p>
            </div>

            {/* Descrição */}
            <div>
              <Label htmlFor="subtitle">Descrição</Label>
              <Textarea
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="bg-[#2A3441] border-[#3A4451] text-white"
                placeholder="Descrição do banner para identificação interna"
                rows={2}
              />
              <p className="text-sm text-gray-400 mt-1">
                Campo opcional para descrever o conteúdo/objetivo do banner
              </p>
            </div>

            {/* Informações Básicas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="page">Página *</Label>
                <Select
                  value={formData.page}
                  onValueChange={(value) => setFormData({ ...formData, page: value as any })}
                >
                  <SelectTrigger className="bg-[#2A3441] border-[#3A4451] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mentores">Mentores</SelectItem>
                    <SelectItem value="jogos">Jogos</SelectItem>
                    <SelectItem value="mentorias">Mentorias</SelectItem>
                    <SelectItem value="comunidades">Comunidades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Imagens */}
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  Imagem Desktop *
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setDesktopFile(e.target.files?.[0] || null)}
                  className="bg-[#2A3441] border-[#3A4451] text-white"
                />
                {editingBanner && !desktopFile && (
                  <p className="text-sm text-gray-400 mt-1">
                    Imagem atual será mantida se não selecionar nova
                  </p>
                )}
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Imagem Mobile (Opcional)
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setMobileFile(e.target.files?.[0] || null)}
                  className="bg-[#2A3441] border-[#3A4451] text-white"
                />
                <p className="text-sm text-gray-400 mt-1">
                  Se não fornecida, a imagem desktop será usada no mobile
                </p>
              </div>
            </div>

            {/* Editor de Posicionamento da Imagem Desktop */}
            {(desktopFile || editingBanner?.image_url) && (
              <BannerImagePositionEditor
                imageUrl={
                  desktopFile ? URL.createObjectURL(desktopFile) : editingBanner?.image_url
                }
                position={formData.desktop_image_position}
                objectPosition={formData.desktop_image_object_position}
                scale={formData.desktop_image_scale}
                onPositionChange={(position) => 
                  setFormData({ ...formData, desktop_image_position: position })
                }
                onObjectPositionChange={(objectPosition) => 
                  setFormData({ ...formData, desktop_image_object_position: objectPosition })
                }
                onScaleChange={(scale) => 
                  setFormData({ ...formData, desktop_image_scale: scale })
                }
              />
            )}

            {/* Link */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="link_url">URL do Link</Label>
                <Input
                  id="link_url"
                  type="url"
                  value={formData.link_url}
                  onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                  className="bg-[#2A3441] border-[#3A4451] text-white"
                  placeholder="https://exemplo.com ou /mentores"
                />
                <p className="text-sm text-gray-400 mt-1">
                  Links externos (https://) abrem em nova aba. Links internos (/pagina) navegam na mesma aba.
                </p>
              </div>
              
              <div>
                <Label htmlFor="link_text">Texto do Link</Label>
                <Input
                  id="link_text"
                  value={formData.link_text}
                  onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                  className="bg-[#2A3441] border-[#3A4451] text-white"
                  placeholder="Ex: Saiba Mais"
                />
                <p className="text-sm text-gray-400 mt-1">
                  Campo opcional - não é exibido no banner (banner inteiro é clicável)
                </p>
              </div>
            </div>

            {/* Configurações */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="display_order">Ordem de Exibição</Label>
                <Input
                  id="display_order"
                  type="number"
                  min="1"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: Number(e.target.value) })}
                  className="bg-[#2A3441] border-[#3A4451] text-white"
                />
              </div>
              
              <div className="flex items-center space-x-2 pt-6">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Ativo</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#00B6F0] hover:bg-[#0095c7]"
            >
              {isSubmitting ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}; 